import { batch, useDispatch, useSelector } from 'react-redux';
import ADMIRAL_QUESTION_ID_TO_AUTOFILL_TYPE from '../../Data/Maps/admiralQuestionIdToAutofillType';
import { validatePhoneSaga } from '../../Lib/redux/sagas/ValidatePhone/validatePhone';
import { validatePostcodeSaga } from '../../Lib/redux/sagas/ValidatePostcode/validatePostcode';
import { setBrowserAutoFillerVisible } from '../../Lib/redux/slices/browserAutoFill';
import {
  setBrowserAutoFilled,
  setBrowserAutoFilledActive,
  setComponentValidationStatus,
  updateValue,
} from '../../Lib/redux/slices/components';
import { mask } from '../../Utils/masking';
import { handleAnswerQuestionSideEffectsSaga } from '../actionCreators';

const useBrowserAutoFiller = () => {
  const dispatch = useDispatch();
  const { mappedQuestionIdToComponentId } = useSelector(
    (store) => store.dataMappings
  );
  const { countryCode, formId } = useSelector((store) => store.formConfig);
  const { currentPage } = useSelector((store) => store.navigation);
  const browserAutoFillComponents = useSelector(
    (store) => store.browserAutoFill.components
  );
  const components = useSelector((store) => store.components);
  const { iFrameId } = useSelector((store) => store.optionalFormConfig);
  const { pages } = useSelector((store) => store.paging);

  const questionIds = Object.keys(mappedQuestionIdToComponentId);

  const autoFillableComponents = Object.keys(
    ADMIRAL_QUESTION_ID_TO_AUTOFILL_TYPE
  ).filter((id) => questionIds.includes(id));

  const autoFillableComponentsFromCurrentPage = autoFillableComponents.filter(
    (id) => {
      const componentId = mappedQuestionIdToComponentId[id];
      if (componentId) {
        const { page } = browserAutoFillComponents[`component_${componentId}`];
        return page > currentPage;
      }
      return false;
    }
  );

  /**
   * handleChange
   *
   * @description  This function is called when the user changes the value of the the shadow components that this handler is attached to.
   *
   * The shadow components here are filled by the browserAutoFill, and when that happens it updates the value of the real component in the store, and to set the browserAutoFilled value to true.
   */
  const handleChange = (params) => {
    const { id, value } = params;
    const componentId = mappedQuestionIdToComponentId[id];
    const isPostcodeField =
      id === 'primary_address_postalcode' || id === 'postcode';
    const isTelephoneField = id.includes('phone');
    if (isTelephoneField) {
      const constraints =
        components[`component_${componentId}`]?.validation?.constraints || {};

      batch(() => {
        dispatch(updateValue({ componentId, value }));
        dispatch(setBrowserAutoFilled({ componentId, value: true }));
        dispatch(setBrowserAutoFilledActive({ componentId, value: true }));
        dispatch(
          validatePhoneSaga({
            country: countryCode,
            number: value,
            componentId,
            iFrameId,
            constraints,
            formId,
          })
        );
      });
    } else if (isPostcodeField) {
      const maskPattern =
        components[`component_${componentId}`]?.input.maskPattern;
      if (maskPattern) {
        batch(() => {
          dispatch(updateValue({ componentId, value }));
          dispatch(setBrowserAutoFilled({ componentId, value: true }));
          dispatch(setBrowserAutoFilledActive({ componentId, value: true }));
          dispatch(
            validatePostcodeSaga({
              countryCode,
              value: mask({ value, maskPattern }),
              componentId,
              iFrameId,
              formId,
            })
          );
        });
      } else {
        batch(() => {
          dispatch(updateValue({ componentId, value }));
          dispatch(setBrowserAutoFilled({ componentId, value: true }));
          dispatch(setBrowserAutoFilledActive({ componentId, value: true }));
          dispatch(
            validatePostcodeSaga({
              countryCode,
              value,
              componentId,
              iFrameId,
              formId,
            })
          );
        });
      }
    } else {
      batch(() => {
        dispatch(updateValue({ componentId, value }));
        dispatch(setBrowserAutoFilled({ componentId, value: true }));
        dispatch(setBrowserAutoFilledActive({ componentId, value: true }));
        dispatch(
          setComponentValidationStatus({
            countryCode,
            componentId,
            iFrameId,
            formId,
          })
        );
        dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
      });
    }
  };

  const inputs = () => {
    return autoFillableComponentsFromCurrentPage.map((id) => {
      const componentId = mappedQuestionIdToComponentId[id];

      const autoCompleteType =
        browserAutoFillComponents[`component_${componentId}`]
          .autoCompleteType === 'disabled'
          ? 'off'
          : ADMIRAL_QUESTION_ID_TO_AUTOFILL_TYPE[id];

      return { id, handleChange, autoCompleteType };
    });
  };

  const setBrowserAutoFillerVisibility = () => {
    const autoFillableComponentIds = autoFillableComponents.map(
      (admiralId) => mappedQuestionIdToComponentId[admiralId]
    );
    const isVisible = pages[currentPage].some((id) =>
      autoFillableComponentIds.includes(id)
    );
    dispatch(setBrowserAutoFillerVisible({ isVisible }));
  };

  return {
    inputs,
    setBrowserAutoFillerVisibility,
  };
};

export default useBrowserAutoFiller;
